import { useEffect, useId, useRef, useState } from 'react';
import { CalendarDays, Check, ChevronDown, X } from 'lucide-react';
import { cn, toIsoDate } from '@/lib/utils';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { PRESET_LABELS, QUICK_PRESETS, type DatePreset } from '@/lib/dateFilter';
import { useDateFilterStore, useResolvedRange } from '@/store/dateFilterStore';

/**
 * The topbar date filter.
 *
 * Replaces the mockup's read-only "today" chip. Until someone uses it the button
 * still reads as that chip — today's date, no accent — so the header looks
 * unchanged; once a range is applied the button picks up the brand colour and a
 * clear affordance, which is the only signal that the numbers below are scoped.
 *
 * Presets are stored as intent (see `lib/dateFilter`), so "Last 7 days" keeps
 * meaning the last seven days on a tab left open overnight.
 */
export function DateRangePicker({ className }: { className?: string }) {
  const filter = useDateFilterStore((state) => state.filter);
  const setPreset = useDateFilterStore((state) => state.setPreset);
  const setCustom = useDateFilterStore((state) => state.setCustom);
  const reset = useDateFilterStore((state) => state.reset);
  const range = useResolvedRange();

  const [open, setOpen] = useState(false);
  const ref = useOutsideClick<HTMLDivElement>(() => setOpen(false), open);
  const panelId = useId();
  const firstOptionRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) firstOptionRef.current?.focus();
  }, [open]);

  const today = toIsoDate(new Date());
  const choose = (preset: DatePreset) => {
    setPreset(preset);
    setOpen(false);
  };

  return (
    <div className={cn('relative', className)} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? panelId : undefined}
        title={range.active ? `Filtered: ${range.longLabel}` : 'Filter by date'}
        className={cn(
          'flex h-[42px] items-center gap-2 rounded-xl border px-3 transition-colors sm:px-3.5',
          range.active
            ? 'border-brand-300 bg-brand-50 text-brand-700 hover:bg-brand-100'
            : 'border-line bg-white text-ink hover:border-brand-200 hover:bg-brand-50/40',
        )}
      >
        <CalendarDays
          className={cn('size-4 shrink-0', range.active ? 'text-brand-600' : 'text-ink-muted')}
          aria-hidden
        />
        {/* Below `sm` the label would push the avatar off a 360px screen, so the
            button collapses to the icon and a dot that says "a filter is on". */}
        <span className="hidden text-[13px] font-semibold whitespace-nowrap sm:inline">
          {range.label}
        </span>
        {range.active && (
          <span className="size-1.5 shrink-0 rounded-full bg-brand-600 sm:hidden" aria-hidden />
        )}
        <ChevronDown
          className={cn(
            'hidden size-4 shrink-0 transition-transform md:block',
            range.active ? 'text-brand-500' : 'text-ink-muted',
            open && 'rotate-180',
          )}
          aria-hidden
        />
      </button>

      {open && (
        <div
          id={panelId}
          role="dialog"
          aria-label="Filter by date"
          className={cn(
            'animate-rise z-30 overflow-hidden rounded-2xl border border-line bg-white shadow-pop',
            // On a phone the button is not the rightmost thing in the topbar, so a
            // right-anchored popover 336px wide runs off the *left* edge. Below
            // `sm` it detaches from the button and becomes a viewport-width sheet
            // under the header instead.
            'fixed inset-x-3 top-[72px]',
            'sm:absolute sm:inset-x-auto sm:top-auto sm:right-0 sm:mt-2 sm:w-[min(21rem,calc(100vw-1.5rem))]',
          )}
        >
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <p className="text-sm font-bold text-ink">Filter by date</p>
            {range.active && (
              <button
                type="button"
                onClick={() => {
                  reset();
                  setOpen(false);
                }}
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-bold text-ink-muted transition-colors hover:bg-canvas hover:text-ink"
              >
                <X className="size-3.5" aria-hidden />
                Clear
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-1.5 p-3">
            {QUICK_PRESETS.map((preset, index) => {
              const active = filter.preset === preset;
              return (
                <button
                  key={preset}
                  type="button"
                  ref={index === 0 ? firstOptionRef : undefined}
                  onClick={() => choose(preset)}
                  aria-pressed={active}
                  className={cn(
                    'flex items-center justify-between gap-1 rounded-lg px-2.5 py-2 text-left text-[13px] font-semibold transition-colors',
                    active
                      ? 'bg-brand-600 text-white'
                      : 'text-ink-soft hover:bg-canvas hover:text-ink',
                  )}
                >
                  <span className="truncate">{PRESET_LABELS[preset]}</span>
                  {active && <Check className="size-3.5 shrink-0" aria-hidden />}
                </button>
              );
            })}
          </div>

          <div className="border-t border-line px-3 pt-3 pb-3">
            <p className="mb-2 text-[11px] font-bold tracking-wide text-ink-muted uppercase">
              Custom range
            </p>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold text-ink-muted">From</span>
                <input
                  type="date"
                  value={filter.preset === 'custom' ? (filter.from ?? '') : (range.from ?? '')}
                  max={filter.preset === 'custom' ? (filter.to ?? today) : today}
                  onChange={(event) => setCustom({ from: event.target.value || undefined })}
                  className="h-9 w-full rounded-lg border border-line bg-white px-2 text-[13px] text-ink outline-none focus:border-brand-400"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold text-ink-muted">To</span>
                <input
                  type="date"
                  value={filter.preset === 'custom' ? (filter.to ?? '') : (range.to ?? '')}
                  min={filter.preset === 'custom' ? filter.from : undefined}
                  max={today}
                  onChange={(event) => setCustom({ to: event.target.value || undefined })}
                  className="h-9 w-full rounded-lg border border-line bg-white px-2 text-[13px] text-ink outline-none focus:border-brand-400"
                />
              </label>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-line bg-canvas/60 px-4 py-2.5">
            <p className="min-w-0 truncate text-[11px] font-medium text-ink-muted">
              {range.active ? range.longLabel : 'Showing all time'}
            </p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="shrink-0 rounded-lg bg-brand-600 px-3 py-1.5 text-[12px] font-bold text-white transition-colors hover:bg-brand-700"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
